import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  getARaging, getRevenueSummary, getClaimStats,
  getInsuranceAR, getCPTUtilization,
} from '../services/reportsService';
import Spinner from '../components/common/Spinner';

const TABS = [
  { id: 'ar',          label: 'AR Aging (Self-pay)' },
  { id: 'insuranceAr', label: 'AR Aging (Insurance)' },
  { id: 'revenue',     label: 'Revenue' },
  { id: 'claims',      label: 'Claims' },
  { id: 'cpt',         label: 'CPT Utilization' },
];

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v ?? 0);

const fmtFull = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v ?? 0);

const pct = (v) => (v != null ? `${v}%` : '—');

/** Format "2025-04" → "Apr '25" for chart labels. */
function fmtMonthLabel(key) {
  const [year, month] = key.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

/** Trigger a CSV file download from a 2D array of values. */
function downloadCSV(filename, rows) {
  const csv = rows
    .map((row) =>
      row.map((cell) => {
        const s = String(cell ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      }).join(',')
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── AR Aging (Self-pay) ──────────────────────────────────────────────────────

const BUCKET_ORDER = ['current', 'days1_30', 'days31_60', 'days61_90', 'over90'];
const BUCKET_COLORS = {
  current:   'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  days1_30:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  days31_60: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  days61_90: 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300',
  over90:    'bg-danger-200 text-danger-900 dark:bg-danger-900/50 dark:text-danger-200',
};

function ARAgingTab() {
  const [expanded, setExpanded] = useState(null);
  const { data, isLoading, isError } = useQuery({ queryKey: ['report-ar'], queryFn: getARaging });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError) return <p className="text-center py-16 text-danger-500">Failed to load report.</p>;

  const { buckets, totalOutstanding } = data;

  const handleExport = () => {
    const rows = [['Invoice', 'Client', 'Due Date', 'Bucket', 'Balance']];
    for (const key of BUCKET_ORDER) {
      for (const inv of buckets[key].invoices) {
        rows.push([`#${inv.invoiceNumber}`, inv.clientName || '', inv.dueDate || '', buckets[key].label, inv.balance]);
      }
    }
    downloadCSV('ar-aging.csv', rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={handleExport} className="btn btn-secondary text-sm flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {BUCKET_ORDER.map((key) => {
          const b = buckets[key];
          return (
            <button
              key={key}
              onClick={() => setExpanded(expanded === key ? null : key)}
              className={`card text-left transition-shadow hover:shadow-md ${expanded === key ? 'ring-2 ring-primary-400' : ''}`}
            >
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">{b.label}</p>
              <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{fmt(b.amount)}</p>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${BUCKET_COLORS[key]}`}>
                {b.count} invoice{b.count !== 1 ? 's' : ''}
              </span>
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Total Outstanding</h3>
          <span className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{fmtFull(totalOutstanding)}</span>
        </div>
        <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden flex">
          {BUCKET_ORDER.map((key) => {
            const b = buckets[key];
            const width = totalOutstanding > 0 ? (b.amount / totalOutstanding) * 100 : 0;
            return width > 0 ? (
              <div
                key={key}
                style={{ width: `${width}%` }}
                className={key === 'current' ? 'bg-green-400' : key === 'days1_30' ? 'bg-yellow-400' : key === 'days31_60' ? 'bg-orange-400' : 'bg-danger-500'}
                title={`${b.label}: ${fmt(b.amount)}`}
              />
            ) : null;
          })}
        </div>
      </div>

      {expanded && buckets[expanded].invoices.length > 0 && (
        <div className="card overflow-x-auto">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            {buckets[expanded].label} — {buckets[expanded].count} invoice{buckets[expanded].count !== 1 ? 's' : ''}
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-4">Invoice</th>
                <th className="pb-2 pr-4">Client</th>
                <th className="pb-2 pr-4">Due date</th>
                <th className="pb-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {buckets[expanded].invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className="py-2 pr-4">
                    <Link to={`/billing/${inv.id}`} className="text-primary-600 hover:underline font-mono">
                      #{inv.invoiceNumber}
                    </Link>
                  </td>
                  <td className="py-2 pr-4">
                    {inv.clientId ? (
                      <Link to={`/clients/${inv.clientId}`} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600">
                        {inv.clientName}
                      </Link>
                    ) : inv.clientName}
                  </td>
                  <td className="py-2 pr-4 text-neutral-500 dark:text-neutral-400">{inv.dueDate || '—'}</td>
                  <td className="py-2 text-right font-semibold text-neutral-900 dark:text-neutral-100">
                    {fmtFull(inv.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Insurance AR ─────────────────────────────────────────────────────────────

const INS_BUCKET_ORDER = ['days0_30', 'days31_60', 'days61_90', 'over90'];
const INS_BUCKET_COLORS = {
  days0_30:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  days31_60: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  days61_90: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  over90:    'bg-danger-200 text-danger-900 dark:bg-danger-900/50 dark:text-danger-200',
};

function InsuranceARTab() {
  const [expanded, setExpanded] = useState(null);
  const { data, isLoading, isError } = useQuery({ queryKey: ['report-insurance-ar'], queryFn: getInsuranceAR });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError) return <p className="text-center py-16 text-danger-500">Failed to load report.</p>;

  const { buckets, byPayer, totalOutstanding } = data;

  const handleExport = () => {
    const rows = [['Claim #', 'Payer', 'Client', 'Submitted', 'Status', 'Days Out', 'Outstanding']];
    for (const key of INS_BUCKET_ORDER) {
      for (const c of buckets[key].claims) {
        rows.push([c.claimNumber || '', c.payerName, c.clientName || '', c.submittedDate || '', c.status, c.daysOut, c.outstanding]);
      }
    }
    downloadCSV('insurance-ar.csv', rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={handleExport} className="btn btn-secondary text-sm flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {INS_BUCKET_ORDER.map((key) => {
          const b = buckets[key];
          return (
            <button
              key={key}
              onClick={() => setExpanded(expanded === key ? null : key)}
              className={`card text-left transition-shadow hover:shadow-md ${expanded === key ? 'ring-2 ring-primary-400' : ''}`}
            >
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">{b.label}</p>
              <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{fmt(b.amount)}</p>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${INS_BUCKET_COLORS[key]}`}>
                {b.count} claim{b.count !== 1 ? 's' : ''}
              </span>
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Total Outstanding (Insurance)</h3>
          <span className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{fmtFull(totalOutstanding)}</span>
        </div>
        <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden flex">
          {INS_BUCKET_ORDER.map((key) => {
            const b = buckets[key];
            const width = totalOutstanding > 0 ? (b.amount / totalOutstanding) * 100 : 0;
            return width > 0 ? (
              <div
                key={key}
                style={{ width: `${width}%` }}
                className={key === 'days0_30' ? 'bg-blue-400' : key === 'days31_60' ? 'bg-yellow-400' : key === 'days61_90' ? 'bg-orange-400' : 'bg-danger-500'}
                title={`${b.label}: ${fmt(b.amount)}`}
              />
            ) : null;
          })}
        </div>
      </div>

      {byPayer.length > 0 && (
        <div className="card overflow-x-auto">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">By Payer</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-6">Payer</th>
                <th className="pb-2 pr-6 text-right">Claims</th>
                <th className="pb-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {byPayer.map((p) => (
                <tr key={p.payerName}>
                  <td className="py-2 pr-6 font-medium text-neutral-900 dark:text-neutral-100">{p.payerName}</td>
                  <td className="py-2 pr-6 text-right text-neutral-600 dark:text-neutral-400">{p.count}</td>
                  <td className="py-2 text-right font-semibold text-neutral-900 dark:text-neutral-100">{fmtFull(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && buckets[expanded].claims.length > 0 && (
        <div className="card overflow-x-auto">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            {buckets[expanded].label} — {buckets[expanded].count} claim{buckets[expanded].count !== 1 ? 's' : ''}
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-4">Claim #</th>
                <th className="pb-2 pr-4">Payer</th>
                <th className="pb-2 pr-4">Client</th>
                <th className="pb-2 pr-4">Submitted</th>
                <th className="pb-2 pr-4">Days out</th>
                <th className="pb-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {buckets[expanded].claims.map((c) => (
                <tr key={c.id}>
                  <td className="py-2 pr-4">
                    <Link to={`/insurance/${c.id}`} className="text-primary-600 hover:underline font-mono">
                      {c.claimNumber || 'Draft'}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-neutral-700 dark:text-neutral-300">{c.payerName}</td>
                  <td className="py-2 pr-4">
                    {c.clientId
                      ? <Link to={`/clients/${c.clientId}`} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600">{c.clientName}</Link>
                      : c.clientName}
                  </td>
                  <td className="py-2 pr-4 text-neutral-500 dark:text-neutral-400">{c.submittedDate || '—'}</td>
                  <td className="py-2 pr-4 text-neutral-500 dark:text-neutral-400">{c.daysOut}</td>
                  <td className="py-2 text-right font-semibold text-neutral-900 dark:text-neutral-100">{fmtFull(c.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Revenue ──────────────────────────────────────────────────────────────────

function RevenueTab() {
  const [months, setMonths] = useState(12);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['report-revenue', months],
    queryFn: () => getRevenueSummary(months),
  });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError) return <p className="text-center py-16 text-danger-500">Failed to load report.</p>;

  const { monthly, totals } = data;

  const chartData = monthly.map((m) => ({
    name: fmtMonthLabel(m.month),
    month: m.month,
    'Self-pay': m.selfPay,
    Insurance: m.insurance,
  }));

  const handleExport = () => {
    const rows = [['Month', 'Self-pay', 'Insurance', 'Total']];
    for (const m of [...monthly].reverse()) {
      rows.push([m.month, m.selfPay, m.insurance, m.total]);
    }
    downloadCSV('revenue.csv', rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-3 gap-4">
          {[
            ['Total', totals.total],
            ['Self-pay', totals.selfPay],
            ['Insurance', totals.insurance],
          ].map(([label, val]) => (
            <div key={label} className="card text-center">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{fmt(val)}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="btn btn-secondary text-sm flex items-center gap-1.5">
            <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
          </button>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="input w-36">
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
          </select>
        </div>
      </div>

      <div className="card">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-4">Monthly Revenue</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(v) => fmtFull(v)} />
            <Legend />
            <Bar dataKey="Self-pay" stackId="a" fill="#6366f1" />
            <Bar dataKey="Insurance" stackId="a" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
              <th className="pb-2 pr-6">Month</th>
              <th className="pb-2 pr-6 text-right">Self-pay</th>
              <th className="pb-2 pr-6 text-right">Insurance</th>
              <th className="pb-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
            {[...monthly].reverse().map((m) => (
              <tr key={m.month}>
                <td className="py-2 pr-6 font-medium text-neutral-900 dark:text-neutral-100">{fmtMonthLabel(m.month)}</td>
                <td className="py-2 pr-6 text-right text-neutral-600 dark:text-neutral-400">{fmtFull(m.selfPay)}</td>
                <td className="py-2 pr-6 text-right text-neutral-600 dark:text-neutral-400">{fmtFull(m.insurance)}</td>
                <td className="py-2 text-right font-semibold text-neutral-900 dark:text-neutral-100">{fmtFull(m.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Claims ───────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  draft:     'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  pending:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  paid:      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  partial:   'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  denied:    'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
};

function ClaimsTab() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['report-claims'], queryFn: getClaimStats });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError) return <p className="text-center py-16 text-danger-500">Failed to load report.</p>;

  const { byStatus, byPayer, summary } = data;
  const statuses = ['draft', 'submitted', 'pending', 'paid', 'partial', 'denied'];

  const handleExport = () => {
    const rows = [['Payer', 'Claims', 'Billed', 'Collected', 'Collection Rate', 'Denial Rate']];
    for (const p of byPayer) {
      rows.push([p.payerName, p.totalClaims, p.totalBilled, p.totalPaid, pct(p.collectionRate), pct(p.denialRate)]);
    }
    downloadCSV('claim-stats.csv', rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={handleExport} className="btn btn-secondary text-sm flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          ['Total billed', fmtFull(summary.totalBilled)],
          ['Total collected', fmtFull(summary.totalPaid)],
          ['Collection rate', pct(summary.collectionRate)],
          ['Denial rate', pct(summary.denialRate)],
        ].map(([label, val]) => (
          <div key={label} className="card text-center">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{val}</p>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-4">Claims by Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {statuses.map((s) => {
            const b = byStatus[s] || { count: 0, billed: 0, paid: 0 };
            return (
              <Link key={s} to={`/insurance?status=${s}`} className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium mb-2 ${STATUS_COLORS[s]}`}>
                  {s}
                </span>
                <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{b.count}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">{fmt(b.billed)} billed</p>
              </Link>
            );
          })}
        </div>
      </div>

      {byPayer.length > 0 && (
        <div className="card overflow-x-auto">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-4">
            Denial &amp; Collection Rate by Payer
            <span className="ml-2 text-xs font-normal text-neutral-400">(payers with 3+ adjudicated claims)</span>
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-4">Payer</th>
                <th className="pb-2 pr-4 text-right">Claims</th>
                <th className="pb-2 pr-4 text-right">Billed</th>
                <th className="pb-2 pr-4 text-right">Collected</th>
                <th className="pb-2 pr-4 text-right">Collection rate</th>
                <th className="pb-2 text-right">Denial rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {byPayer.map((p) => (
                <tr key={p.payerName}>
                  <td className="py-2 pr-4 font-medium text-neutral-900 dark:text-neutral-100">{p.payerName}</td>
                  <td className="py-2 pr-4 text-right text-neutral-600 dark:text-neutral-400">{p.totalClaims}</td>
                  <td className="py-2 pr-4 text-right text-neutral-600 dark:text-neutral-400">{fmtFull(p.totalBilled)}</td>
                  <td className="py-2 pr-4 text-right text-neutral-600 dark:text-neutral-400">{fmtFull(p.totalPaid)}</td>
                  <td className="py-2 pr-4 text-right">
                    <span className={p.collectionRate >= 80 ? 'text-green-600 font-medium' : p.collectionRate >= 60 ? 'text-yellow-600 font-medium' : 'text-danger-600 font-medium'}>
                      {pct(p.collectionRate)}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <span className={p.denialRate <= 5 ? 'text-green-600 font-medium' : p.denialRate <= 15 ? 'text-yellow-600 font-medium' : 'text-danger-600 font-medium'}>
                      {pct(p.denialRate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── CPT Utilization ──────────────────────────────────────────────────────────

const CPT_DESCRIPTIONS = {
  '90791': 'Psychiatric diagnostic evaluation',
  '90792': 'Psychiatric diagnostic evaluation w/ medical services',
  '90832': 'Psychotherapy, 30 min',
  '90834': 'Psychotherapy, 45 min',
  '90837': 'Psychotherapy, 60 min',
  '90839': 'Psychotherapy for crisis, 60 min',
  '90846': 'Family psychotherapy without patient',
  '90847': 'Family psychotherapy with patient',
  '90853': 'Group psychotherapy',
};

function CPTUtilizationTab() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['report-cpt'], queryFn: getCPTUtilization });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError) return <p className="text-center py-16 text-danger-500">Failed to load report.</p>;

  const { cptData } = data;

  const totalBilled = cptData.reduce((s, r) => s + r.totalCharged, 0);

  const handleExport = () => {
    const rows = [['CPT Code', 'Description', 'Lines', 'Units', 'Total Billed', 'Collection Rate']];
    for (const r of cptData) {
      rows.push([r.cptCode, CPT_DESCRIPTIONS[r.cptCode] || '', r.lineCount, r.totalUnits, r.totalCharged, pct(r.collectionRate)]);
    }
    downloadCSV('cpt-utilization.csv', rows);
  };

  if (cptData.length === 0) {
    return (
      <div className="text-center py-16 text-neutral-500 dark:text-neutral-400">
        No service lines found. Add CPT codes to claims to see utilization data.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {cptData.length} code{cptData.length !== 1 ? 's' : ''} — {fmtFull(totalBilled)} total billed
        </p>
        <button onClick={handleExport} className="btn btn-secondary text-sm flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
              <th className="pb-2 pr-4">CPT code</th>
              <th className="pb-2 pr-4">Description</th>
              <th className="pb-2 pr-4 text-right">Lines</th>
              <th className="pb-2 pr-4 text-right">Units</th>
              <th className="pb-2 pr-4 text-right">Total billed</th>
              <th className="pb-2 pr-4 text-right">% of billing</th>
              <th className="pb-2 text-right">Collection rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
            {cptData.map((r) => {
              const share = totalBilled > 0 ? ((r.totalCharged / totalBilled) * 100).toFixed(1) : '0';
              return (
                <tr key={r.cptCode}>
                  <td className="py-2 pr-4 font-mono font-medium text-primary-600 dark:text-primary-400">{r.cptCode}</td>
                  <td className="py-2 pr-4 text-neutral-700 dark:text-neutral-300 max-w-xs truncate">
                    {CPT_DESCRIPTIONS[r.cptCode] || '—'}
                  </td>
                  <td className="py-2 pr-4 text-right text-neutral-600 dark:text-neutral-400">{r.lineCount}</td>
                  <td className="py-2 pr-4 text-right text-neutral-600 dark:text-neutral-400">{r.totalUnits}</td>
                  <td className="py-2 pr-4 text-right font-semibold text-neutral-900 dark:text-neutral-100">{fmtFull(r.totalCharged)}</td>
                  <td className="py-2 pr-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full" style={{ width: `${share}%` }} />
                      </div>
                      <span className="text-neutral-500 dark:text-neutral-400 w-10 text-right">{share}%</span>
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    {r.collectionRate != null ? (
                      <span className={r.collectionRate >= 80 ? 'text-green-600 font-medium' : r.collectionRate >= 60 ? 'text-yellow-600 font-medium' : 'text-danger-600 font-medium'}>
                        {r.collectionRate}%
                      </span>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Reports() {
  const [activeTab, setActiveTab] = useState('ar');

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Reports</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Financial and operational metrics for your practice
        </p>
      </div>

      <div className="flex gap-1 mb-6 border-b border-neutral-200 dark:border-neutral-700 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ar'          && <ARAgingTab />}
      {activeTab === 'insuranceAr' && <InsuranceARTab />}
      {activeTab === 'revenue'     && <RevenueTab />}
      {activeTab === 'claims'      && <ClaimsTab />}
      {activeTab === 'cpt'         && <CPTUtilizationTab />}
    </div>
  );
}
